import { useState, useEffect, useMemo } from "react";
import "@/App.css";
import { 
  Check, 
  X, 
  Clock, 
  Truck, 
  ShieldCheck, 
  Star, 
  Flame, 
  Zap, 
  ChevronDown, 
  ChevronUp,
  CreditCard,
  Package,
  AlertTriangle,
  Phone,
  MapPin,
  User
} from "lucide-react";
import CustomForm from "./CustomForm";

// Modal Component for Privacy/Terms
const LegalModal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div 
        className="relative bg-zinc-900 border border-zinc-700 rounded-sm max-w-lg w-full max-h-[80vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-zinc-700">
          <h3 className="text-lg font-bold text-white uppercase">{title}</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Chiudi"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto max-h-[60vh] text-gray-300 text-sm leading-relaxed space-y-4">
          {children}
        </div>
        <div className="p-4 border-t border-zinc-700">
          <button 
            onClick={onClose}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-sm transition-colors"
          >
            Ho capito
          </button>
        </div>
      </div>
    </div>
  );
};

// Privacy Policy Content
const PrivacyPolicyContent = () => (
  <>
    <p><strong>Informativa sulla Privacy</strong></p>
    <p>
      La presente informativa descrive le modalità di gestione del sito in riferimento al trattamento 
      dei dati personali degli utenti che lo consultano.
    </p>
    
    <p><strong>Titolare del Trattamento</strong></p>
    <p>
      Il titolare del trattamento dei dati è Tauro Plus, con sede in Italia. 
      Per qualsiasi informazione è possibile contattarci tramite i canali indicati sul sito.
    </p>
    
    <p><strong>Dati Raccolti</strong></p>
    <p>
      I dati personali raccolti includono: nome e cognome, numero di telefono, indirizzo di spedizione. 
      Questi dati sono necessari esclusivamente per l'evasione dell'ordine e la consegna del prodotto.
    </p>
    
    <p><strong>Finalità del Trattamento</strong></p>
    <p>
      I dati vengono trattati per: elaborazione e spedizione degli ordini, comunicazioni relative 
      all'ordine, adempimento di obblighi di legge.
    </p>
    
    <p><strong>Conservazione dei Dati</strong></p>
    <p>
      I dati personali saranno conservati per il tempo strettamente necessario a conseguire le 
      finalità per cui sono stati raccolti.
    </p>
    
    <p><strong>Diritti dell'Interessato</strong></p>
    <p>
      L'utente ha diritto di accesso, rettifica, cancellazione dei propri dati personali, 
      nonché il diritto di opporsi al trattamento.
    </p>
    
    <p><strong>Cookie e Strumenti di Analisi</strong></p>
    <p>
      Il sito utilizza cookie tecnici necessari al funzionamento. Inoltre, utilizziamo i seguenti 
      strumenti di analisi per migliorare l'esperienza utente e l'efficacia delle campagne pubblicitarie:
    </p>
    <p>
      • <strong>Meta Pixel (Facebook)</strong>: raccoglie dati anonimi sulla navigazione per analizzare 
      l'efficacia delle campagne pubblicitarie. Eventi tracciati: visualizzazione pagina (PageView) e 
      profondità di scorrimento (ScrollDepth al 25%, 50%, 70%, 90% della pagina).
    </p>
    <p>
      • <strong>Hotjar/Contentsquare</strong>: analizza il comportamento degli utenti attraverso mappe 
      di calore e registrazioni anonimizzate delle sessioni per migliorare l'usabilità del sito.
    </p>
    <p>
      Questi strumenti raccolgono dati in forma anonima e aggregata. Per maggiori informazioni o per 
      esercitare i tuoi diritti, consulta le rispettive informative privacy di Meta (facebook.com/privacy) 
      e Contentsquare (contentsquare.com/privacy).
    </p>
  </>
);

// Terms and Conditions Content
const TermsContent = () => (
  <>
    <p><strong>Termini e Condizioni di Vendita</strong></p>
    <p>
      I presenti termini e condizioni regolano l'acquisto dei prodotti Tauro Plus 
      effettuato attraverso questo sito web.
    </p>
    
    <p><strong>Prodotto</strong></p>
    <p>
      Tauro Plus è un integratore alimentare a base di ingredienti naturali (Taurina, Ginseng, Tribulus). 
      Non è un medicinale e non è destinato a diagnosticare, trattare, curare o prevenire alcuna malattia.
    </p>
    
    <p><strong>Ordini e Pagamento</strong></p>
    <p>
      Gli ordini vengono effettuati compilando il modulo presente sul sito. Il pagamento avviene 
      in contrassegno (COD) alla consegna del prodotto al corriere.
    </p>
    
    <p><strong>Spedizione</strong></p>
    <p>
      La spedizione è gratuita per tutti gli ordini. I tempi di consegna sono di 24/48 ore lavorative 
      dalla conferma dell'ordine. La spedizione avviene dal nostro magazzino in Italia.
    </p>
    
    <p><strong>Garanzia Soddisfatti o Rimborsati</strong></p>
    <p>
      Offriamo una garanzia di 90 giorni. Se non sei soddisfatto del prodotto, puoi richiedere 
      il rimborso completo contattando il nostro servizio clienti entro 90 giorni dall'acquisto.
    </p>
    
    <p><strong>Limitazioni di Responsabilità</strong></p>
    <p>
      I risultati possono variare da persona a persona. Le testimonianze presenti sul sito 
      rappresentano esperienze individuali e non garantiscono risultati specifici.
    </p>
    
    <p><strong>Controindicazioni</strong></p>
    <p>
      Si consiglia di consultare un medico prima dell'uso in caso di patologie preesistenti, 
      assunzione di farmaci, gravidanza o allattamento.
    </p>
    
    <p><strong>Legge Applicabile</strong></p>
    <p>
      I presenti termini sono regolati dalla legge italiana. Per qualsiasi controversia 
      sarà competente il Foro del luogo di residenza del consumatore.
    </p>
  </>
);

// Countdown Timer Component
const CountdownTimer = () => {
  const [time, setTime] = useState({ minutes: 14, seconds: 59 });

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { minutes: prev.minutes - 1, seconds: 59 };
        } else {
          return { minutes: 14, seconds: 59 };
        }
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="countdown-box rounded-sm px-4 py-3 inline-flex items-center gap-3" data-testid="countdown-timer">
      <Clock className="w-5 h-5 text-red-500 animate-pulse" />
      <span className="text-white font-bold text-lg md:text-xl font-['Oswald']">
        L'OFFERTA SCADE TRA: 
        <span className="text-red-500 ml-2 animate-countdown">
          {String(time.minutes).padStart(2, '0')}:{String(time.seconds).padStart(2, '0')}
        </span>
      </span>
    </div>
  );
};

// Stock Bar Component
const StockBar = () => {
  return (
    <div className="w-full max-w-md mx-auto" data-testid="stock-bar">
      <div className="flex justify-between items-center mb-2">
        <span className="text-red-400 font-bold text-sm uppercase flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Scorte Limitate!
        </span>
        <span className="text-white font-bold">87% Venduto</span>
      </div>
      <div className="stock-bar-bg h-4 rounded-full overflow-hidden">
        <div className="stock-bar-fill h-full stock-progress" style={{ width: '87%' }}></div>
      </div>
      <p className="text-gray-400 text-xs mt-2 text-center">Solo 13 confezioni rimaste oggi!</p>
    </div>
  );
};

// Trust Badge Component
const TrustBadge = ({ icon: Icon, text, variant = "trust" }) => {
  const baseClass = variant === "urgent" ? "badge-urgent" : "badge-trust";
  return (
    <div className={`${baseClass} px-3 py-2 rounded-sm flex items-center gap-2 text-xs font-bold uppercase tracking-wider`}>
      <Icon className="w-4 h-4" />
      <span>{text}</span>
    </div>
  );
};

// Testimonial Component
const TestimonialCard = ({ name, location, text, rating, purchased }) => {
  return (
    <div className="testimonial-card p-5 rounded-sm" data-testid="testimonial-card">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-12 h-12 bg-zinc-800 rounded-full flex items-center justify-center">
          <User className="w-6 h-6 text-gray-400" />
        </div>
        <div>
          <p className="font-bold text-white">{name}</p>
          <p className="text-gray-400 text-sm">{location}</p>
        </div>
        <span className="verified-badge ml-auto">Acquisto Verificato</span>
      </div>
      <div className="star-rating flex gap-1 mb-3">
        {[...Array(rating)].map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-current" />
        ))}
      </div>
      <p className="text-gray-300 italic leading-relaxed">"{text}"</p>
      <p className="text-gray-500 text-sm mt-3">Ha acquistato: {purchased}</p>
    </div>
  );
};

// Ingredient Card Component
const IngredientCard = ({ name, description, icon: Icon }) => {
  return (
    <div className="ingredient-card p-5 rounded-sm text-center" data-testid="ingredient-card">
      <div className="w-16 h-16 bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon className="w-8 h-8 text-red-500" />
      </div>
      <h4 className="text-white font-bold text-lg mb-2 uppercase">{name}</h4>
      <p className="text-gray-400 text-sm">{description}</p>
    </div>
  );
};

// FAQ Item Component
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="faq-item rounded-sm overflow-hidden" data-testid="faq-item">
      <button 
        className="w-full p-4 flex items-center justify-between text-left"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span className="font-bold text-white">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-red-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-red-500" />
        )}
      </button>
      <div className={`faq-answer ${isOpen ? 'open' : ''}`}>
        <div className="px-4 pb-4 text-gray-400">
          {answer}
        </div>
      </div>
    </div>
  );
};

// Comparison Row Component
const ComparisonRow = ({ feature, tauroPlus, others }) => {
  return (
    <div className="grid grid-cols-3 border-b border-zinc-800">
      <div className="comparison-cell text-gray-300 text-sm">{feature}</div>
      <div className="comparison-cell text-center">
        {tauroPlus ? (
          <Check className="w-5 h-5 text-green-500 mx-auto" />
        ) : (
          <X className="w-5 h-5 text-red-500 mx-auto" />
        )}
      </div>
      <div className="comparison-cell text-center">
        {others ? (
          <Check className="w-5 h-5 text-green-500 mx-auto" />
        ) : (
          <X className="w-5 h-5 text-red-500 mx-auto" />
        )}
      </div>
    </div>
  );
};

// CTA Button Component
const CTAButton = ({ onClick, className = "" }) => {
  return (
    <button 
      onClick={onClick}
      className={`cta-button w-full py-4 px-6 rounded-sm font-bold text-white uppercase text-lg flex items-center justify-center gap-3 ${className}`}
      data-testid="cta-button"
    >
      <Flame className="w-6 h-6" />
      <span>ORDINA ORA - PAGA ALLA CONSEGNA</span>
      <span className="cta-arrow">→</span>
    </button>
  );
};

// Main App Component
function App() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  // Read URL parameters for page variants
  const params = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      useCustomForm: urlParams.get('form') === 'custom',
      angle: urlParams.get('angle') || 'default' // 'default' or 'ed' (eiaculazione precoce)
    };
  }, []);

  // Fire ViewContent event on page load
  useEffect(() => {
    if (typeof window.fbq === "function") {
      window.fbq("track", "ViewContent", {
        content_name: params.angle === 'ed' ? "Tauro Plus - ED Angle" : "Tauro Plus",
        content_category: "Integratori",
        value: 49.00,
        currency: "EUR"
      });
    }
  }, [params.angle]);

  const scrollToForm = () => {
    document.getElementById('order-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Content variations based on angle
  const content = useMemo(() => {
    if (params.angle === 'ed') {
      // Eiaculazione Precoce angle
      return {
        headline: <>SOFFRI DI <span className="text-red-500">EIACULAZIONE PRECOCE</span> O HAI PROBLEMI A MANTENERE L'EREZIONE?</>,
        subheadline: <>TauroPlus aiuta gli uomini a <strong className="text-white">durare più a lungo</strong>, controllare l'orgasmo e avere erezioni più potenti – <span className="text-green-400">naturalmente e senza farmaci!</span></>,
        benefits: [
          "Controllo Totale sull'Eiaculazione",
          "Erezioni che Durano ORE!",
          "Più Resistenza a Letto",
          "100% Naturale, Zero Effetti Collaterali"
        ],
        problemTitle: <>DURARE DI PIÙ <span className="text-red-500">NON È FACILE</span></>,
        problemText1: <>La maggior parte delle soluzioni per l'eiaculazione precoce <strong className="text-red-400">non sono progettate per dare risultati veri</strong> e permanenti.</>,
        problemText2: <>Probabilmente l'hai sperimentato se hai provato <strong className="text-white">spray ritardanti, creme anestetiche o pillole</strong> che promettono miracoli ma <strong className="text-red-400">ti lasciano senza sensibilità</strong> o con effetti collaterali imbarazzanti.</>,
        problemStat: <><strong className="text-red-400">Oltre il 60% degli uomini</strong> sperimenta nel corso della vita problemi di durata. Chi soffre di eiaculazione precoce vive i rapporti con <strong className="text-red-400">ansia e frustrazione</strong>.</>,
        problemConclusion: <>Questo crea un circolo vizioso: <strong className="text-white">l'ansia da prestazione peggiora il problema</strong>, e senza una soluzione sicura ed efficace, diventa sempre più difficile soddisfare la tua partner.</>,
        solutionTitle: <>PRESENTIAMO LA FORMULA <span className="text-red-500">"CONTROLLO TOTALE"</span> DI TAURO PLUS</>,
        solutionText: <>Abbiamo sviluppato un integratore naturale che usa una formula <strong className="text-white">"Controllo Totale"</strong> specificamente per aiutare gli uomini a durare più a lungo, controllare l'orgasmo e avere prestazioni da vero stallone.</>,
        // Extended problem section content for ED
        extendedProblem: true,
        formCopy: <>Soffri di eiaculazione precoce o fai fatica a durare quanto vorresti? <strong className="text-white">Oltre il 60% degli uomini</strong> vive lo stesso problema. Se potessi risolverlo davvero, <strong className="text-white">quanto pagheresti?</strong> Scommetto molto più di <span className="text-green-400 font-bold">49€</span>.</>
      };
    }
    
    // Default angle (lunghezza pene)
    return {
      headline: <>TI VERGOGNI DELLA LUNGHEZZA DEL TUO PENE O HAI <span className="text-red-500">EREZIONI DEBOLI?</span></>,
      subheadline: <>TauroPlus aiuta gli uomini a ottenere un pene <strong className="text-white">più lungo (fino a 5cm)</strong>, erezioni di granito e prestazioni da pornodivo – <span className="text-green-400">naturalmente e senza bisturi!</span></>,
      benefits: [
        "Allungamento Pene (fino a 5cm)",
        "Erezioni che Durano ORE!",
        "Più Desiderio Sessuale",
        "100% Naturale, Zero Effetti Collaterali"
      ],
      problemTitle: <>RECUPERARE <span className="text-red-500">LUNGHEZZA E POTENZA</span> NON È FACILE</>,
      problemText1: <><strong className="text-white">La maggior parte delle soluzioni</strong> per allungare il pene o migliorare l'erezione <strong className="text-red-400"> non sono progettate per dare risultati veri</strong> e permanenti a ogni uomo.</>,
      problemText2: <>Probabilmente l'hai sperimentato se hai provato <strong className="text-white">integratori che promettono miracoli immediati</strong> ma non riescono a darti <strong className="text-red-400">né centimetri in più né erezioni</strong> abbastanza dure per soddisfarla davvero.</>,
      problemStat: <><strong className="text-red-400">L'86% degli uomini italiani</strong> si vergogna della lunghezza del suo pene, o <strong className="text-red-400">soffre di disfunzione erettile</strong>.</>,
      problemConclusion: <>Questo crea un circolo vizioso: <strong className="text-white">pene piccolo e erezioni scarse portano a imbarazzo e prestazioni sessuali umilianti</strong>, e senza una soluzione sicura ed efficace, diventa sempre più difficile recuperare la tua virilità e autostima da uomo.</>,
      solutionTitle: <>PRESENTIAMO LA FORMULA <span className="text-red-500">"TRIPLO POTENZIAMENTO"</span> DI TAURO PLUS</>,
      solutionText: <>Abbiamo sviluppato un integratore naturale che usa una formula <strong className="text-white">"Triplo Potenziamento"</strong> specificamente per aiutare gli uomini ad allungare il pene (fino a 5cm), ottenere erezioni di granito e prestazioni da pornoattore.</>,
      extendedProblem: false,
      formCopy: <>L'86% degli uomini italiani si sente a disagio per la dimensione del proprio pene o ha problemi di erezione. Se potessi risolverlo davvero, <strong className="text-white">quanto pagheresti?</strong> Scommetto molto più di <span className="text-green-400 font-bold">49€</span>.</>
    };
  }, [params.angle]);

  // Testimonials - different for ED angle
  const testimonials = params.angle === 'ed' ? [
    {
      name: "Luca P.",
      location: "Roma, Italia",
      text: "Prima duravo massimo 2-3 minuti, ora riesco a controllare l'eiaculazione e durare quanto voglio. La mia ragazza non ci credeva! Finalmente posso godermi il sesso senza ansia.",
      rating: 5,
      purchased: "2 Confezioni Tauro Plus"
    },
    {
      name: "Alessandro B.",
      location: "Milano, Italia",
      text: "L'eiaculazione precoce mi ha rovinato 2 relazioni. Con Tauro Plus ho ritrovato il controllo totale. Dopo 3 settimane i risultati erano già evidenti. Ora duro tranquillamente 20-30 minuti.",
      rating: 5,
      purchased: "3 Confezioni Tauro Plus"
    },
    {
      name: "Roberto M.",
      location: "Napoli, Italia",
      text: "Avevo provato spray e creme ritardanti ma mi toglievano tutta la sensibilità. Con Tauro Plus sento tutto, ma ho il pieno controllo. È come avere un interruttore in mano!",
      rating: 5,
      purchased: "1 Confezione Tauro Plus"
    },
    {
      name: "Stefano G.",
      location: "Torino, Italia",
      text: "A 45 anni pensavo fosse normale durare poco. Mia moglie era frustrata. Dopo un mese di Tauro Plus sembro tornato ai miei 25 anni. Lei è felicissima e io ho ritrovato la sicurezza.",
      rating: 5,
      purchased: "2 Confezioni Tauro Plus"
    }
  ] : [
    {
      name: "Marco R.",
      location: "Roma, Italia",
      text: "Ho guadagnato quasi 4cm in lunghezza, le mie erezioni sono dure come il marmo, e mi sento finalmente orgoglioso. Consigliatissimo!",
      rating: 5,
      purchased: "2 Confezioni Tauro Plus"
    },
    {
      name: "Giuseppe F.",
      location: "Milano, Italia",
      text: "Dopo solo un paio di settimane di uso quotidiano, posso finalmente godere di rapporti completi senza quella sensazione di inadeguatezza che mi svegliava la notte.",
      rating: 5,
      purchased: "1 Confezione Tauro Plus"
    },
    {
      name: "Davide S.",
      location: "Napoli, Italia",
      text: "La mia resistenza a letto è aumentata, le mie erezioni sono più potenti, e finalmente mi sento sicuro nelle mie prestazioni di nuovo.",
      rating: 5,
      purchased: "3 Confezioni Tauro Plus"
    },
    {
      name: "Antonio M.",
      location: "Firenze, Italia",
      text: "Ero scettico all'inizio, ma dopo un mese di utilizzo i risultati sono incredibili. Mi sento come 10 anni fa! La mia partner è entusiasta.",
      rating: 5,
      purchased: "2 Confezioni Tauro Plus"
    }
  ];

  const faqs = [
    {
      question: "Come posso ordinare Tauro Plus?",
      answer: "Puoi ordinare Tauro Plus direttamente da questa pagina. Compila il modulo con i tuoi dati, scegli la quantità e conferma. Pagherai comodamente al corriere alla consegna."
    },
    {
      question: "Quanto costa la spedizione?",
      answer: "La spedizione è GRATUITA per tutti gli ordini. La consegna avviene in 24/48 ore lavorative direttamente dal nostro magazzino in Italia."
    },
    {
      question: "Quanto tempo ci vuole per vedere i risultati?",
      answer: "Molti uomini riportano miglioramenti già dalla prima settimana. Per risultati ottimali e duraturi, consigliamo un utilizzo costante per almeno 4 settimane."
    },
    {
      question: "Ci sono effetti collaterali?",
      answer: "Tauro Plus è formulato con ingredienti 100% naturali (Taurina, Ginseng, Tribulus) e non presenta effetti collaterali noti. È sicuro per l'uso quotidiano."
    },
    {
      question: "Cosa succede se non sono soddisfatto?",
      answer: "Offriamo una garanzia Soddisfatti o Rimborsati di 90 giorni. Se non sei completamente soddisfatto, ti rimborsiamo l'intero importo senza domande."
    }
  ];

  const comparisonFeatures = [
    { feature: "100% Naturale", tauroPlus: true, others: false },
    { feature: "Nessun Effetto Collaterale", tauroPlus: true, others: false },
    { feature: "Azione Rapida", tauroPlus: true, others: true },
    { feature: "Risultati Duraturi", tauroPlus: true, others: false },
    { feature: "Senza Dipendenza", tauroPlus: true, others: false },
    { feature: "Garanzia 90 Giorni", tauroPlus: true, others: false },
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Top Banner */}
      <div className="bg-red-700 py-2 px-4 text-center" data-testid="top-banner">
        <p className="text-white font-bold text-sm md:text-base uppercase tracking-wider animate-pulse">
          🔥 SCONTI DI STAGIONE: 37% DI SCONTO - SOLO OGGI! 🔥
        </p>
      </div>

      <main className="main-content">
        {/* Hero Section */}
        <section className="section-dark py-8 md:py-16 px-4" data-testid="hero-section">
          <div className="max-w-4xl mx-auto text-center">
            {/* Countdown Timer */}
            <div className="mb-6">
              <CountdownTimer />
            </div>

            {/* Main Headline */}
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight text-glow-red">
              {content.headline}
            </h1>

            <p className="text-lg md:text-xl text-gray-300 mb-6 max-w-2xl mx-auto">
              {content.subheadline}
            </p>

            {/* Product Image - Large Hero Banner */}
            <div className="my-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_tauro-lead-italia/artifacts/hjkqcmi4_tauro%20homepage.png"
                alt="Tauro Plus - Potenza e Durata Senza Limiti"
                className="mx-auto w-full max-w-md md:max-w-2xl rounded-sm shadow-2xl"
                data-testid="product-image"
              />
            </div>

            {/* Benefits List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-xl mx-auto mb-8">
              {content.benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-3 bg-zinc-900/50 p-3 rounded-sm border border-zinc-800">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 check-icon" />
                  <span className="text-white text-sm md:text-base font-medium">{benefit}</span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <CTAButton onClick={scrollToForm} />

            {/* Trust Badges */}
            <div className="trust-badges-row mt-6">
              <TrustBadge icon={Truck} text="Spedizione Gratis" />
              <TrustBadge icon={ShieldCheck} text="Garanzia 90 Giorni" />
              <TrustBadge icon={CreditCard} text="Paga alla Consegna" />
            </div>

            {/* Stock Bar */}
            <div className="mt-8">
              <StockBar />
            </div>
          </div>
        </section>

        {/* Section Divider */}
        <div className="section-divider"></div>

        {/* Problem Section */}
        <section className="section-darker py-12 md:py-20 px-4" data-testid="problem-section">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-bold text-center mb-8">
              {content.problemTitle}
            </h2>

            <div className="space-y-6 text-gray-300">
              <p className="text-lg">
                {content.problemText1}
              </p>

              <p className="text-lg">
                {content.problemText2}
              </p>

              <div className="bg-red-900/20 border border-red-800 p-6 rounded-sm">
                <p className="text-lg text-center">
                  {content.problemStat}
                </p>
              </div>

              <p className="text-lg">
                {content.problemConclusion}
              </p>

              {/* Extended ED Content */}
              {content.extendedProblem && (
                <>
                  <div className="border-t border-zinc-700 pt-8 mt-8">
                    <p className="text-lg">
                      E nella maggior parte dei casi <strong className="text-white">NON è colpa dell'età…</strong>
                    </p>
                    
                    <p className="text-lg mt-4">
                      Se ti riconosci in questa situazione, <strong className="text-red-400">smettila di pensare che sia "un tuo limite"</strong>. 
                      Non sei l'unico. E soprattutto <strong className="text-white">non sei sbagliato</strong>.
                    </p>
                  </div>

                  <div className="bg-zinc-900 border border-zinc-700 p-6 rounded-sm">
                    <h3 className="text-xl font-bold text-white mb-4">
                      STRESS E ORMONI: La Vera Ragione per Cui Stai Durando Sempre Meno
                    </h3>
                    <p className="text-lg">
                      Gli uomini con alti livelli di <strong className="text-white">stress e cortisolo</strong> sono molto più inclini a perdere controllo durante il rapporto, 
                      durare meno e vivere il sesso con ansia invece che con sicurezza.
                    </p>
                    <div className="bg-red-900/30 border border-red-700 p-4 rounded-sm mt-4">
                      <p className="text-center text-lg">
                        <strong className="text-red-400">Gli uomini con alti livelli di stress e cortisolo sono fino a 9 volte più propensi</strong> a soffrire di eiaculazione precoce.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <p className="text-lg">
                      Lo <strong className="text-white">stress cronico</strong> è uno dei principali nemici della performance maschile. 
                      Quando stress e cortisolo salgono, il corpo entra in modalità difensiva: <strong className="text-red-400">la risposta è rapida, ma il controllo crolla</strong>.
                    </p>
                    
                    <p className="text-lg">
                      In un uomo in equilibrio, il sangue riempie correttamente i tessuti permettendo un'erezione <strong className="text-white">stabile e duratura</strong>.
                    </p>
                    
                    <p className="text-lg">
                      Con stress alto o squilibri ormonali, il flusso cala prima: <strong className="text-red-400">la rigidità cede, il controllo diminuisce</strong> e tutto sembra "finire troppo in fretta".
                    </p>
                  </div>

                  <div className="bg-zinc-900 border border-zinc-700 p-6 rounded-sm mt-6">
                    <h3 className="text-xl font-bold text-white mb-4">
                      Esistono Ingredienti o Soluzioni che Possono Davvero Aiutarti?
                    </h3>
                    <p className="text-lg text-green-400 font-bold mb-4">Assolutamente sì.</p>
                    
                    <p className="text-lg mb-4">
                      La strada più comune per durare di più a letto è ricorrere a <strong className="text-white">farmaci chimici come il sildenafil</strong>.
                    </p>
                    
                    <p className="text-lg mb-4">
                      In alcuni casi funzionano, ma spesso al prezzo di <strong className="text-red-400">mal di testa, cali di pressione, tachicardia</strong> e una forte dipendenza psicologica dalla pillola.
                    </p>
                    
                    <p className="text-lg mb-4">
                      E anche se il farmaco funziona per te, <strong className="text-white">devi continuare ad assumerlo ogni volta</strong>. Se smetti, il problema ritorna immediatamente.
                    </p>
                    
                    <p className="text-lg mb-4">
                      In molti casi la sensazione è artificiale, la risposta meno naturale e <strong className="text-red-400">la sicurezza dipende interamente da una compressa</strong> presa prima del rapporto.
                    </p>
                    
                    <p className="text-lg">
                      Un altro rischio è la <strong className="text-red-400">perdita progressiva di fiducia</strong>, perché il corpo smette di reagire senza stimolo chimico esterno.
                    </p>
                  </div>

                  <div className="bg-green-900/20 border border-green-700 p-6 rounded-sm text-center">
                    <p className="text-lg font-bold text-white mb-2">
                      È davvero questo il modo in cui vuoi vivere la tua sessualità?
                    </p>
                    <p className="text-lg text-green-400">
                      Oppure preferiresti ottenere gli stessi risultati del farmaco, <strong>senza effetti collaterali</strong>, lavorando in modo <strong>naturale e duraturo</strong> sul tuo corpo?
                    </p>
                  </div>

                  <p className="text-lg mt-6">
                    Mentre esistono decine di presunte "soluzioni" sul mercato, <strong className="text-white">Tauro Plus</strong>, grazie alla sua formula naturale scientificamente studiata 
                    e ai risultati documentati dei clienti, continua a dimostrarsi come <strong className="text-green-400">l'unica risposta concreta</strong> per risolvere davvero problemi di controllo, durata e sicurezza a letto.
                  </p>
                </>
              )}

              {/* Customer Success Image - Man with product */}
              <div className="mt-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tauro-lead-italia/artifacts/dwzrfqa3_tauro1.png"
                  alt="Cliente soddisfatto con Tauro Plus"
                  className="w-full max-w-sm mx-auto rounded-sm shadow-xl border border-zinc-700"
                />
                <p className="text-center text-gray-400 text-sm mt-3 italic">
                  "Grazie a Tauro Plus ho ritrovato la mia sicurezza"
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="section-dark py-12 md:py-20 px-4" data-testid="solution-section">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-4xl font-bold mb-8">
              {content.solutionTitle}
            </h2>

            <p className="text-lg text-gray-300 mb-8 max-w-3xl mx-auto">
              {content.solutionText}
            </p>

            {/* Product Image in Solution Section */}
            <div className="mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_tauro-lead-italia/artifacts/tefui3ik_tauro%20plus.png"
                alt="Tauro Plus - Integratore Naturale"
                className="mx-auto w-48 md:w-64 product-glow"
              />
            </div>

            {/* Ingredients */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <IngredientCard 
                name="Taurina Premium"
                description="Supporta la vasodilatazione naturale, aiuta a ottenere erezioni più piene, dure e una maggiore lunghezza."
                icon={Zap}
              />
              <IngredientCard 
                name="Ginseng"
                description="Erba adattogena utilizzata da secoli per aumentare energia, resistenza e performance sessuale."
                icon={Flame}
              />
              <IngredientCard 
                name="Tribulus Terrestris"
                description="Favorisce il supporto naturale del testosterone e della libido, migliorando desiderio e sensibilità."
                icon={Star}
              />
            </div>

            <CTAButton onClick={scrollToForm} />
          </div>
        </section>

        {/* Comparison Section */}
        <section className="section-darker py-12 md:py-20 px-4" data-testid="comparison-section">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-bold text-center mb-8">
              TAURO PLUS È LA SCELTA <span className="text-red-500">PIÙ SICURA</span>
            </h2>

            <div className="bg-zinc-900 rounded-sm overflow-hidden border border-zinc-800">
              {/* Header */}
              <div className="grid grid-cols-3 bg-zinc-800">
                <div className="p-4 text-sm font-bold uppercase">Caratteristica</div>
                <div className="p-4 text-sm font-bold uppercase text-center text-green-400">Tauro Plus</div>
                <div className="p-4 text-sm font-bold uppercase text-center text-gray-400">Altri Prodotti</div>
              </div>
              
              {/* Rows */}
              {comparisonFeatures.map((item, index) => (
                <ComparisonRow key={index} {...item} />
              ))}
            </div>
          </div>
        </section>

        {/* Clinic Approval Section - After Comparison */}
        <section className="section-dark py-12 md:py-16 px-4" data-testid="clinic-section">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-bold text-center mb-8">
              <span className="text-green-400">APPROVATO</span> DALLE CLINICHE
            </h2>
            
            <div className="bg-zinc-900 rounded-sm border border-zinc-800 overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_tauro-lead-italia/artifacts/16o601fe_tauro%20clinica.png"
                alt="Tauro Plus Approvato dai Medici - Made in Italy"
                className="w-full max-w-md mx-auto"
                data-testid="clinic-image"
              />
              <div className="p-6 text-center">
                <p className="text-gray-300 text-lg mb-4">
                  <strong className="text-white">Tauro Plus</strong> è raccomandato da professionisti della salute 
                  per il suo approccio <span className="text-green-400">100% naturale</span> e sicuro alla virilità maschile.
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  <span className="badge-trust px-3 py-2 rounded-sm flex items-center gap-2 text-xs font-bold uppercase">
                    <ShieldCheck className="w-4 h-4" />
                    Clinicamente Testato
                  </span>
                  <span className="badge-trust px-3 py-2 rounded-sm flex items-center gap-2 text-xs font-bold uppercase">
                    <Check className="w-4 h-4" />
                    Made in Italy
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="section-dark py-12 md:py-20 px-4" data-testid="testimonials-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-bold text-center mb-4">
              CLIENTI REALI. <span className="text-red-500">RISULTATI REALI</span> CHE CAMBIANO LA VITA.
            </h2>
            <p className="text-center text-gray-400 mb-8">
              <Star className="w-5 h-5 inline text-yellow-500" /> 18,686+ Recensioni Verificate
            </p>

            {/* Customer Photos Gallery - Large Images */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_tauro-lead-italia/artifacts/728p5qct_tauro2.png"
                alt="Cliente soddisfatto Marco"
                className="w-full aspect-square object-cover rounded-sm border-2 border-red-600/50 hover:border-red-500 transition-colors"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_tauro-lead-italia/artifacts/ojp5ogza_tauro3.png"
                alt="Cliente soddisfatto Giuseppe"
                className="w-full aspect-square object-cover rounded-sm border-2 border-red-600/50 hover:border-red-500 transition-colors"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_tauro-lead-italia/artifacts/hjkqcmi4_tauro%20homepage.png"
                alt="Cliente soddisfatto Davide"
                className="w-full aspect-square object-cover rounded-sm border-2 border-red-600/50 hover:border-red-500 transition-colors"
              />
            </div>

            <p className="text-center text-green-400 font-bold mb-8 text-sm uppercase tracking-wider">
              <Check className="w-4 h-4 inline mr-2" />
              Foto reali dei nostri clienti italiani
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {testimonials.map((testimonial, index) => (
                <TestimonialCard key={index} {...testimonial} />
              ))}
            </div>

            <CTAButton onClick={scrollToForm} />
          </div>
        </section>

        {/* Guarantee Section */}
        <section className="section-darker py-12 md:py-20 px-4" data-testid="guarantee-section">
          <div className="max-w-3xl mx-auto text-center">
            <div className="guarantee-seal mx-auto mb-6">
              <ShieldCheck className="w-10 h-10 text-white mb-1" />
              <span className="text-white font-bold text-xs uppercase">90 Giorni</span>
              <span className="text-white font-bold text-xs uppercase">Garanzia</span>
            </div>

            <h2 className="text-2xl md:text-4xl font-bold mb-6">
              PROVA TAURO PLUS PER 90 GIORNI, <span className="text-green-400">100% SENZA RISCHI</span>
            </h2>

            <p className="text-lg text-gray-300 mb-6">
              Crediamo così tanto in <strong className="text-white">Tauro Plus</strong> che non vogliamo che tu spenda un centesimo 
              finché non sei completamente soddisfatto.
            </p>

            <p className="text-lg text-gray-300">
              Se non noti un <strong className="text-white">miglioramento significativo</strong> in energia, resistenza e vitalità, 
              ti rimborseremo <strong className="text-green-400">ogni singolo euro — senza domande</strong>.
            </p>
          </div>
        </section>

        {/* Order Form Section */}
        <section id="order-form" className="section-dark py-12 md:py-20 px-4" data-testid="order-section">
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8">
              <TrustBadge icon={AlertTriangle} text="Offerta Speciale Solo per OGGI!" variant="urgent" />
            </div>

            <h2 className="text-2xl md:text-4xl font-bold text-center mb-4">
              RICHIEDI IL TUO <span className="text-red-500">TAURO PLUS SCONTATO</span> OGGI
            </h2>

            <p className="text-center text-gray-400 mb-6">Finché le scorte durano!</p>

            {/* Stock Bar */}
            <div className="mb-8">
              <StockBar />
            </div>

            {/* Trust Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              <div className="bg-zinc-900 p-3 rounded-sm text-center border border-zinc-800">
                <Truck className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <p className="text-xs text-gray-300">Spedizione in 24/48h</p>
              </div>
              <div className="bg-zinc-900 p-3 rounded-sm text-center border border-zinc-800">
                <ShieldCheck className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <p className="text-xs text-gray-300">Garanzia 90 Giorni</p>
              </div>
              <div className="bg-zinc-900 p-3 rounded-sm text-center border border-zinc-800">
                <CreditCard className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <p className="text-xs text-gray-300">Paga alla Consegna</p>
              </div>
              <div className="bg-zinc-900 p-3 rounded-sm text-center border border-zinc-800">
                <Package className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <p className="text-xs text-gray-300">Spedizione GRATIS</p>
              </div>
            </div>

            {/* Persuasive Copy Before Form */}
            <div className="bg-red-900/20 border border-red-800 rounded-sm p-5 mb-8">
              <p className="text-white text-lg font-bold mb-3 text-center">
                {content.formCopy}
              </p>
            </div>

            {/* Order Form - Custom or Iframe based on URL param */}
            {params.useCustomForm ? (
              <CustomForm />
            ) : (
              <>
                {/* Affiliate Form Iframe - White Background */}
                <div className="iframe-container bg-white" data-testid="affiliate-form-container">
                  <iframe 
                    src="https://offerte2019.club/affiliateproject/tracker/click/?pub_id=2e5c2cb8d13e&land_id=2013"
                    className="affiliate-iframe"
                    title="Modulo Ordine Tauro Plus"
                    aria-label="Modulo per ordinare Tauro Plus"
                    loading="lazy"
                  />
                </div>

                {/* Fallback Link */}
                <div className="text-center mt-4">
                  <a 
                    href="https://offerte2019.club/affiliateproject/tracker/click/?pub_id=2e5c2cb8d13e&land_id=2013"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-400 underline text-sm hover:text-red-300"
                    data-testid="fallback-link"
                  >
                    Clicca qui se il modulo non carica correttamente
                  </a>
                </div>
              </>
            )}

            {/* Trust Reassurance After Form */}
            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-3 bg-zinc-900/50 p-4 rounded-sm border border-zinc-800">
                <CreditCard className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white font-bold text-sm">NESSUN PAGAMENTO ONLINE</p>
                  <p className="text-gray-400 text-sm">Paghi solo alla consegna, direttamente al corriere. Zero rischi.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 bg-zinc-900/50 p-4 rounded-sm border border-zinc-800">
                <ShieldCheck className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white font-bold text-sm">RISPETTIAMO LA TUA PRIVACY</p>
                  <p className="text-gray-400 text-sm">I tuoi dati sono al sicuro. Non verrai tartassato di chiamate o email spam.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 bg-zinc-900/50 p-4 rounded-sm border border-zinc-800">
                <Package className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white font-bold text-sm">PACCO 100% ANONIMO</p>
                  <p className="text-gray-400 text-sm">Consegna in confezione neutra senza alcun riferimento al contenuto. Totale discrezione.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 bg-green-900/20 p-4 rounded-sm border border-green-700">
                <Check className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white font-bold text-sm">GARANZIA SODDISFATTI O RIMBORSATI</p>
                  <p className="text-gray-400 text-sm">Se non funziona, ti rimborsiamo tutto. Ma siamo certi che funzionerà — come ha funzionato per migliaia di uomini italiani.</p>
                </div>
              </div>
            </div>

            <p className="text-center text-gray-500 text-xs mt-6">
              <strong className="text-red-400">N.B:</strong> Tauro Plus non è disponibile su Amazon o eBay! Ordina solo dal sito ufficiale.
            </p>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="section-darker py-12 md:py-20 px-4" data-testid="faq-section">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-bold text-center mb-8">
              DOMANDE <span className="text-red-500">FREQUENTI</span>
            </h2>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <FAQItem key={index} question={faq.question} answer={faq.answer} />
              ))}
            </div>

            <div className="mt-8">
              <CTAButton onClick={scrollToForm} />
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-black border-t border-zinc-800 py-8 px-4" data-testid="footer">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-2xl font-bold text-white mb-4 font-['Oswald']">TAURO PLUS</p>
            
            <div className="flex justify-center gap-6 mb-6 text-sm">
              <button 
                onClick={() => setShowPrivacy(true)}
                className="text-gray-400 hover:text-red-400 transition-colors"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => setShowTerms(true)}
                className="text-gray-400 hover:text-red-400 transition-colors"
              >
                Termini e Condizioni
              </button>
            </div>

            <p className="text-gray-500 text-xs">
              © 2025 Tauro Plus. Tutti i diritti riservati.
            </p>
            <p className="text-gray-600 text-xs mt-2">
              *I risultati possono variare da persona a persona. Questo prodotto non è destinato a diagnosticare, 
              trattare, curare o prevenire alcuna malattia.
            </p>
          </div>
        </footer>
      </main>

      {/* Privacy Policy Modal */}
      <LegalModal 
        isOpen={showPrivacy} 
        onClose={() => setShowPrivacy(false)} 
        title="Privacy Policy"
      >
        <PrivacyPolicyContent />
      </LegalModal>

      {/* Terms Modal */}
      <LegalModal 
        isOpen={showTerms} 
        onClose={() => setShowTerms(false)} 
        title="Termini e Condizioni"
      >
        <TermsContent />
      </LegalModal>

      {/* Sticky CTA for Mobile */}
      <div className="sticky-cta md:hidden" data-testid="sticky-cta">
        <button 
          onClick={scrollToForm}
          className="cta-button w-full py-3 px-4 rounded-sm font-bold text-white uppercase text-base flex items-center justify-center gap-2"
        >
          <Flame className="w-5 h-5" />
          <span>ORDINA ORA - 37% SCONTO</span>
        </button>
      </div>
    </div>
  );
}

export default App;
